import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import type { ResourceCategory } from '../constants/resources';

/** A scheduled (or one-off) task on a resource (Ecological EC5). */
export interface ResourceTask {
  id: string;
  name: string;
  /** Days between recurrence; 0 = one-off (a list item). */
  intervalDays: number;
  lastCompletedAt: string | null;
  /** How many times completed — the XP source (utils/resourcesXp). */
  completionCount: number;
}

export interface Resource {
  id: string;
  name: string;
  category: ResourceCategory;
  notes?: string;
  tasks: ResourceTask[];
  createdAt: string;
  updatedAt: string;
}

// ─── Supabase sync — resources as a blob per user (whole-store-blob pattern) ────
const USER_RESOURCES_TABLE = 'user_resources';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

function readCloudResources(data: any) {
  return Array.isArray(data?.resources) ? data.resources.map(mapResource) : [];
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// array, so a device holding a stale cache silently deleted records it had never
// seen (diagnosed 2026-07-27 on the inbox — see lib/syncBlob.ts).
const blobSync = createBlobSync({
  transport: supabaseBlobTransport(USER_RESOURCES_TABLE),
  owner: () => useResourcesStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync resources to Supabase:', err),
  build: (cloud) => {
    const state = useResourcesStore.getState();
    const cloudTombs = readTombstones(cloud);
    const deleted = pruneTombstones(mergeTombstones(state.deleted, cloudTombs));
    const resources = mergeCollection(state.resources, readCloudResources(cloud), deleted);
    if (matchesCloud([[resources, cloud?.resources]], deleted, cloudTombs)) return null;
    return { resources, deleted };
  },
});

interface ResourcesState {
  resources: Resource[];
  /** id → deletedAt. Without this, the union-on-write would resurrect deletes. */
  deleted: Tombstones;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  addResource: (input: { name: string; category: ResourceCategory; notes?: string }) => Resource;
  updateResource: (id: string, updates: Partial<Pick<Resource, 'name' | 'category' | 'notes'>>) => void;
  deleteResource: (id: string) => void;
  addTask: (resourceId: string, input: { name: string; intervalDays: number }) => void;
  updateTask: (resourceId: string, taskId: string, updates: Partial<Pick<ResourceTask, 'name' | 'intervalDays'>>) => void;
  deleteTask: (resourceId: string, taskId: string) => void;
  /** Mark a task done now — bumps its count (the XP source) + timestamp. */
  completeTask: (resourceId: string, taskId: string) => void;
}

const now = () => new Date().toISOString();

function mapResource(r: any): Resource {
  return {
    id: r.id,
    name: r.name,
    category: r.category,
    notes: r.notes,
    tasks: Array.isArray(r.tasks)
      ? r.tasks.map((t: any) => ({
          id: t.id,
          name: t.name,
          intervalDays: t.intervalDays ?? 0,
          lastCompletedAt: t.lastCompletedAt ?? null,
          completionCount: t.completionCount ?? 0,
        }))
      : [],
    createdAt: r.createdAt ?? now(),
    updatedAt: r.updatedAt ?? now(),
  };
}

export const useResourcesStore = create<ResourcesState>()(
  persist<ResourcesState, [], [], any>(
    (set, get) => ({
      resources: [],
      deleted: {},
      initializedUserId: null,
      cachedUserId: null,

      initialize: () => dedupeInit('user_resources', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().cachedUserId === user.id && isCloudFresh('user_resources', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }
          const { data, error } = await supabase.from(USER_RESOURCES_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            if (row && row.data && Array.isArray(row.data.resources)) {
              const deleted = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              const merged = mergeCollection(
                cacheIsOurs ? state.resources : [], readCloudResources(row.data), deleted);
              return {
                // Same reference when the merge was a no-op, so the subscription
                // below doesn't fire a pointless write on every boot.
                resources: sameRecords(merged, state.resources) ? state.resources : merged,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              : { resources: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          blobSync.writeBack(row?.data);
          markCloudFresh('user_resources', user.id);
        } catch (error) {
          console.error('Failed to initialize resources from Supabase:', error);
        }
      }),

      addResource: ({ name, category, notes }) => {
        const resource: Resource = {
          id: crypto.randomUUID(),
          name: name.trim(),
          category,
          notes: notes?.trim() || undefined,
          tasks: [],
          createdAt: now(),
          updatedAt: now(),
        };
        set(state => ({ resources: [...state.resources, resource] }));
        return resource;
      },

      updateResource: (id, updates) => {
        set(state => ({
          resources: state.resources.map(r =>
            r.id === id ? { ...r, ...updates, updatedAt: now() } : r,
          ),
        }));
      },

      deleteResource: (id) => {
        // ⚠️ Must leave a tombstone — writes are a union now, so a bare filter
        // would be undone by the next merge against the cloud copy.
        set(state => ({
          resources: state.resources.filter(r => r.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      addTask: (resourceId, { name, intervalDays }) => {
        const task: ResourceTask = {
          id: crypto.randomUUID(),
          name: name.trim(),
          intervalDays,
          lastCompletedAt: null,
          completionCount: 0,
        };
        set(state => ({
          resources: state.resources.map(r =>
            r.id === resourceId ? { ...r, tasks: [...r.tasks, task], updatedAt: now() } : r,
          ),
        }));
      },

      updateTask: (resourceId, taskId, updates) => {
        set(state => ({
          resources: state.resources.map(r =>
            r.id === resourceId
              ? { ...r, tasks: r.tasks.map(t => (t.id === taskId ? { ...t, ...updates } : t)), updatedAt: now() }
              : r,
          ),
        }));
      },

      deleteTask: (resourceId, taskId) => {
        set(state => ({
          resources: state.resources.map(r =>
            r.id === resourceId ? { ...r, tasks: r.tasks.filter(t => t.id !== taskId), updatedAt: now() } : r,
          ),
        }));
      },

      completeTask: (resourceId, taskId) => {
        set(state => ({
          resources: state.resources.map(r =>
            r.id === resourceId
              ? {
                  ...r,
                  tasks: r.tasks.map(t =>
                    t.id === taskId
                      ? { ...t, lastCompletedAt: now(), completionCount: (t.completionCount || 0) + 1 }
                      : t,
                  ),
                  updatedAt: now(),
                }
              : r,
          ),
        }));
      },
    }),
    {
      name: 'resources-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state: ResourcesState) =>
        ({ resources: state.resources, deleted: state.deleted, cachedUserId: state.cachedUserId }) as any,
    },
  ),
);

// Auto-sync on change. No suppression flag: initialize() returns the SAME array
// reference when its merge changed nothing, and when it did change something a
// write-back is exactly what makes the two sides converge.
useResourcesStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (state.resources === prev.resources && state.deleted === prev.deleted) return;
  blobSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useResourcesStore);
